/* assets/facet-sort-dropdown.js
   --------------------------------------------------------------------------
   Convertit le(s) select natif(s) "Sort by" des pages collection/recherche en
   composant .custom-dropdown (meme UI que les listes deroulantes de variantes
   de la page produit). Le <select> natif reste dans le DOM (cache) pour :
     - porter la valeur soumise au formulaire de facettes,
     - declencher le tri AJAX de Dawn (qui ecoute l'event `input` sur
       `facet-filters-form > form`).

   Re-init : Dawn re-rend les selects de tri a chaque filtrage AJAX
   (renderAdditionalElements remplace `.sorting`, renderFilters remplace les
   `.js-filter`). Un MutationObserver re-enhance tout nouveau select.

   Fallback : si ce JS ne s'execute pas, le <select> natif reste visible et
   stylise a la charte (cf. custom.css) — progressive enhancement.
   -------------------------------------------------------------------------- */
(() => {
  if (window.__facetSortDropdownInit) return;
  window.__facetSortDropdownInit = true;

  const SELECT_SELECTOR = 'select[name="sort_by"]';
  let uid = 0;
  // Apres un tri choisi au clavier, Dawn re-rend le select : le focus est rendu
  // au nouveau declencheur visible (sinon il retombe sur <body>).
  let restoreFocus = false;

  function closeAll(except) {
    document.querySelectorAll('.custom-dropdown--sort.is-open').forEach((d) => {
      if (d === except) return;
      d.classList.remove('is-open');
      d.querySelector('.custom-dropdown__trigger')?.setAttribute('aria-expanded', 'false');
      d.querySelector('.custom-dropdown__trigger')?.removeAttribute('aria-activedescendant');
      d.querySelectorAll('.custom-dropdown__option.is-focused').forEach((o) => o.classList.remove('is-focused'));
      // Retarde le retrait de .opens-up pour laisser la transition se finir (150 ms),
      // sinon le panneau "flashe" vers le bas avant de disparaitre.
      if (d.classList.contains('opens-up')) {
        setTimeout(() => d.classList.remove('opens-up'), 150);
      }
    });
  }

  function buildDropdown(select) {
    if (!select || select.dataset.cdEnhanced === '1') return;
    select.dataset.cdEnhanced = '1';
    select.classList.add('is-cd-enhanced'); // masque le natif (cf. custom.css)

    const dropdown = document.createElement('div');
    dropdown.className = 'custom-dropdown custom-dropdown--sort';

    // --- Trigger (reprend .custom-dropdown__trigger des variantes) ---
    // IMPORTANT : on n'utilise PAS <button>. Le trigger est dans le drawer
    // mobile <menu-drawer>, dont bindEvents() attache onCloseButtonClick a TOUT
    // button[type="button"] (et le rappelle apres chaque rendu AJAX) -> le bouton
    // fermerait le drawer. Un <div> (role="combobox") evite ca (et evite aussi tout
    // risque de submit du <form> de facettes).
    const trigger = document.createElement('div');
    trigger.className = 'custom-dropdown__trigger';
    trigger.setAttribute('role', 'combobox');
    trigger.setAttribute('tabindex', '0');
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');

    const valueEl = document.createElement('span');
    valueEl.className = 'custom-dropdown__value';
    valueEl.textContent = select.options[select.selectedIndex]?.textContent.trim() || '';

    // 29/10/2026 : largeur du plus long choix. Tous les libelles, invisibles,
    // sont empiles avec le choix affiche dans la meme case de grille (cf.
    // mobe-ui.css, .custom-dropdown__value-wrap) : le declencheur ne change plus
    // de taille d'un tri a l'autre.
    const valueWrap = document.createElement('span');
    valueWrap.className = 'custom-dropdown__value-wrap';
    valueWrap.appendChild(valueEl);
    [...select.options].forEach((opt) => {
      const sizer = document.createElement('span');
      sizer.className = 'custom-dropdown__value-sizer';
      sizer.setAttribute('aria-hidden', 'true');
      sizer.textContent = opt.textContent.trim();
      valueWrap.appendChild(sizer);
    });
    trigger.appendChild(valueWrap);

    const toggle = document.createElement('span');
    toggle.className = 'custom-dropdown__toggle-icon';
    toggle.innerHTML =
      '<span class="custom-dropdown__icon custom-dropdown__icon--plus">+</span>' +
      '<span class="custom-dropdown__icon custom-dropdown__icon--minus">−</span>';
    trigger.appendChild(toggle);

    dropdown.appendChild(trigger);

    // --- Liste d'options ---
    const list = document.createElement('ul');
    list.className = 'custom-dropdown__options';
    list.setAttribute('role', 'listbox');
    list.id = `SortListbox-${++uid}`;
    trigger.setAttribute('aria-controls', list.id);

    [...select.options].forEach((opt, i) => {
      const li = document.createElement('li');
      li.className = 'custom-dropdown__option';
      li.id = `${list.id}-${i}`;
      li.dataset.value = opt.value;
      li.setAttribute('role', 'option');
      const isActive = opt.value === select.value;
      li.classList.toggle('is-active', isActive);
      li.setAttribute('aria-selected', isActive ? 'true' : 'false');

      const label = document.createElement('span');
      label.className = 'custom-dropdown__option-label';
      label.textContent = opt.textContent.trim();
      li.appendChild(label);
      list.appendChild(li);
    });

    dropdown.appendChild(list);

    // Insere le dropdown juste apres le select, dans le wrapper .select
    select.parentNode.insertBefore(dropdown, select.nextSibling);

    // --- Comportement ---
    const toggleOpen = () => {
      const wasOpen = dropdown.classList.contains('is-open');
      closeAll();
      if (!wasOpen) {
        // Ouvre vers le haut si l'espace en bas est insuffisant (comme les variantes)
        const rect = trigger.getBoundingClientRect();
        const panelMax = 240; // = max-height 24rem
        const below = window.innerHeight - rect.bottom;
        const above = rect.top;
        if (below < panelMax && above > below) dropdown.classList.add('opens-up');
        dropdown.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
      }
    };

    trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      toggleOpen();
    });
    // Clavier (le <div> n'est pas natif) — meme modele que custom-variant-ui.js :
    // le focus reste sur le declencheur, aria-activedescendant designe l'option
    // surlignee (.is-focused, deja stylee dans custom.css).
    //   Entree / Espace : ouvre, ou choisit l'option surlignee
    //   Fleches : ouvrent, puis deplacent ; Debut / Fin : premiere / derniere
    //   Echap : ferme (ecouteur global) ; Tab : ferme et laisse partir le focus
    let activeIndex = -1;
    const options = () => [...list.querySelectorAll('.custom-dropdown__option')];
    const setActive = (i) => {
      const all = options();
      if (!all.length) return;
      activeIndex = Math.max(0, Math.min(all.length - 1, i));
      all.forEach((o, k) => o.classList.toggle('is-focused', k === activeIndex));
      trigger.setAttribute('aria-activedescendant', all[activeIndex].id);
      all[activeIndex].scrollIntoView({ block: 'nearest' });
    };
    const clearActive = () => {
      activeIndex = -1;
      options().forEach((o) => o.classList.remove('is-focused'));
      trigger.removeAttribute('aria-activedescendant');
    };
    trigger.addEventListener('keydown', (e) => {
      const isOpen = dropdown.classList.contains('is-open');
      const all = options();
      const selected = all.findIndex((o) => o.classList.contains('is-active'));
      switch (e.key) {
        case 'Enter':
        case ' ':
        case 'Spacebar':
          e.preventDefault();
          e.stopPropagation();
          if (isOpen && activeIndex >= 0) {
            const chosen = all[activeIndex];
            // Rendre le focus seulement si le tri change (Dawn re-rendra le select).
            if (!chosen.classList.contains('is-active')) restoreFocus = true;
            clearActive();
            chosen.click();
          } else {
            toggleOpen();
            if (dropdown.classList.contains('is-open')) setActive(selected);
            else clearActive();
          }
          break;
        case 'ArrowDown':
        case 'ArrowUp':
          e.preventDefault();
          e.stopPropagation();
          if (!isOpen) {
            toggleOpen();
            setActive(selected);
          } else {
            const from = activeIndex >= 0 ? activeIndex : selected;
            setActive(from + (e.key === 'ArrowDown' ? 1 : -1));
          }
          break;
        case 'Home':
        case 'End':
          if (!isOpen) return;
          e.preventDefault();
          setActive(e.key === 'Home' ? 0 : all.length - 1);
          break;
        case 'Tab':
          if (isOpen) closeAll();
          break;
        default:
      }
    });

    list.querySelectorAll('.custom-dropdown__option').forEach((li) => {
      li.addEventListener('click', () => {
        const value = li.dataset.value;
        if (select.value === value) {
          closeAll();
          return;
        }
        select.value = value;

        list.querySelectorAll('.custom-dropdown__option').forEach((o) => {
          const active = o === li;
          o.classList.toggle('is-active', active);
          o.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        valueEl.textContent = li.querySelector('.custom-dropdown__option-label').textContent;
        closeAll();

        // Notifie Dawn -> tri AJAX (facet-filters-form ecoute `input`)
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    if (restoreFocus && dropdown.offsetParent) {
      restoreFocus = false;
      trigger.focus();
    }
  }

  function enhanceAll() {
    document.querySelectorAll(SELECT_SELECTOR).forEach(buildDropdown);
  }

  // Fermeture globale (clic exterieur / Echap)
  document.addEventListener('click', () => closeAll());
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeAll();
  });

  // Re-init apres rendu AJAX de Dawn (debounce pour absorber nos propres insertions)
  let scheduled = false;
  const scheduleEnhance = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      enhanceAll();
    });
  };

  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === 'childList' && (m.addedNodes.length || m.removedNodes.length)) {
        scheduleEnhance();
        return;
      }
    }
  });

  function start() {
    enhanceAll();
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', enhanceAll);
})();
